import AsyncValidator from 'async-validator'
import get from 'lodash/get'
import uuid from 'uuid'
import emailRule from '../../helper/validation/emailRule'
import requiredChoiceRule from '../../helper/validation/requiredChoiceRule'
import requiredRadioRule from '../../helper/validation/requiredRadioRule'
import requiredRule from '../../helper/validation/requiredRule'
import emitter from '../../mixin/emitter'

const noop = function doNothing() {}

export default {
  name: 'UiFormItem',

  mixins: [emitter],

  provide() {
    return {
      uiFormItem: this,
    }
  },

  inject: {
    uiForm: {
      default: '',
    },
  },

  // language=HTML
  template: `
    <div class="ui-form-item" :class="{
        'is-error': validateState === 'error',
        'is-validating': validateState === 'validating',
        'is-success': validateState === 'success',
        'is-required': isRequired
      }">
      <div class="ui-form-item__inner" :style="computedInnerItemStyle">
        <div v-if="hasLabel" class="ui-form-item__label" :style="labelWrapStyle">
          <label :for="computedId" :style="labelStyle">
            <slot name="label">{{ label }}</slot>
            <span v-if="isRequired" class="asterisk">*</span>
          </label>
        </div>
        <div class="ui-form-item__field" :style="fieldWrapStyle">
          <slot></slot>
          <template v-if="$slots.help">
            <div class="help-text">
              <slot name="help"></slot>
            </div>
          </template>
          <div v-if="validateState === 'error'" class="ui-form-item__error">
            <span class="error">{{ validateMessage }}</span>
          </div>
        </div>
      </div>
    </div>
  `,

  props: {
    prop: String,
    id: String,
    label: String,
    labelPosition: String,
    labelColumns: Number,
    labelWidth: String,
    innerItemStyle: {
      type: Object,
      default: () => {},
    },
    labelWrapStyle: {
      type: Object,
      default: () => {},
    },
    fieldWrapStyle: {
      type: Object,
      default: () => {},
    },
    labelStyle: {
      type: Object,
      default: () => {},
    },
    inputColumns: Number,
    email: {
      type: Boolean,
      default: undefined,
    },
    required: {
      type: Boolean,
      default: undefined,
    },
    requiredRadio: {
      type: Boolean,
      default: undefined,
    },
    requiredChoice: {
      type: Boolean,
      default: undefined,
    },
    requiredTrue: {
      type: Boolean,
      default: undefined,
    },
    rules: Array,
  },

  data() {
    return {
      validateState: '',
      validateMessage: '',
      validator: {},
    }
  },

  computed: {
    hasLabel() {
      return !!(this.label || this.$slots.label)
    },

    computedId() {
      if (this.id) {
        return this.id
      }

      return this.prop ? this.propToId(this.prop) : uuid.v1()
    },

    computedLabelPosition() {
      if (this.labelPosition) {
        return this.labelPosition
      }

      if (!this.uiForm) {
        return 'top'
      }

      return this.uiForm.labelPosition
    },

    computedLabelWidth() {
      if (this.labelWidth) {
        return this.labelWidth
      }
      if (!this.labelWidth && this.uiForm.labelWidth) {
        return this.uiForm.labelWidth
      }
      switch (this.uiForm.labelColumns) {
        case 1:
          return '8.33%'
        case 2:
          return '16.66%'
        case 3:
          return '25%'
        case 4:
          return '33.33%'
        case 5:
          return '42%'
        case 6:
          return '50%'
        case 7:
          return '58.33%'
        case 8:
          return '66.66%'
        case 9:
          return '75%'
        case 10:
          return '83.33%'
        case 11:
          return '91.66%'
        case 12:
          return '100%'
        default:
          return '25%'
      }
    },

    computedInnerItemStyle() {
      const style = { ...this.innerItemStyle }
      if (this.hasLabel && this.computedLabelPosition === 'inline') {
        style.display = 'flex'
        style.columnGap = '20px'
      } else if (this.hasLabel && this.computedLabelPosition === 'left') {
        style.display = 'grid'
        style.gridTemplateColumns = `${this.computedLabelWidth} 1fr`
        style.columnGap = '20px'
      }
      return style
    },

    fieldValue() {
      const model = this.uiForm ? this.uiForm.model : null

      if (!model || !this.prop) {
        return null
      }

      return get(model, this.prop)
    },

    isBooleanFieldValue() {
      return this.fieldValue === true || this.fieldValue === false
    },

    isRequired() {
      return this.required || this.requiredTrue || this.requiredRadio || this.requiredChoice || false
    },
  },

  watch: {
    error: {
      immediate: true,
      handler(value) {
        this.validateMessage = value
        this.validateState = value ? 'error' : ''
      },
    },
  },

  methods: {
    propToId(prop) {
      return String(prop).replace(/\./g, '_')
    },

    validate(callback) {
      if (typeof callback !== 'function') {
        // eslint-disable-next-line no-param-reassign
        callback = noop
      }

      const rules = this.getRules()

      if ((!rules || rules.length === 0) && !this.isRequired) {
        callback()
        return
      }

      this.validateState = 'validating'

      const descriptor = {}
      descriptor[this.prop] = rules

      const validator = new AsyncValidator(descriptor)
      const model = {}
      model[this.prop] = this.fieldValue

      validator.validate(model, { firstFields: true }, (errors, invalidFields) => {
        this.validateState = !errors ? 'success' : 'error'
        this.validateMessage = errors ? errors[0].message : ''
        callback(this.validateMessage, invalidFields)
        if (this.uiForm) {
          this.uiForm.$emit('validate', this.prop, !errors)
        }
      })
    },

    clearValidate() {
      this.validateState = ''
      this.validateMessage = ''
    },

    getRules() {
      const allFormRules = this.uiForm ? this.uiForm.rules : {}
      const formRules = get(allFormRules, this.prop || 'fdjdfhdjfh', [])
      let rules = []

      if (this.rules && this.rules.length) {
        rules = rules.concat(this.rules)
      } else if (formRules && formRules.length) {
        rules = rules.concat(formRules)
      }

      let computedRequiredRule = null
      if (this.requiredTrue) {
        computedRequiredRule = { ...requiredRule, type: 'enum', enum: [true] }
      } else if (this.requiredChoice) {
        computedRequiredRule = requiredChoiceRule
      } else if (this.requiredRadio) {
        computedRequiredRule = requiredRadioRule
      } else if (this.required) {
        computedRequiredRule = requiredRule
      }

      if (computedRequiredRule) {
        rules.push(computedRequiredRule)
      }

      if (this.email) {
        rules.push(emailRule)
      }

      return rules
    },

    onFieldBlur() {
      this.validate()
    },

    onFieldChange() {
      this.validate()
    },
  },

  mounted() {
    if (this.prop) {
      this.dispatch('UiForm', 'ui.form.addField', [this])

      let initialValue = this.fieldValue
      if (Array.isArray(initialValue)) {
        initialValue = [].concat(initialValue)
      }
      Object.defineProperty(this, 'initialValue', { value: initialValue })

      const rules = this.getRules()
      if (rules.length) {
        this.$on('ui.form.blur', this.onFieldBlur)
        this.$on('ui.form.change', this.onFieldChange)
      }
    }
  },

  beforeDestroy() {
    this.dispatch('UiForm', 'ui.form.removeField', [this])
  },
}
